import React, { useState, useEffect, useCallback } from "react";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue, set, update, remove } from "firebase/database";
import { collection, onSnapshot } from "firebase/firestore";
import {
  hydrateAutoForwardSources,
  sourceEmailsOf,
  toggleAutoForwardSource,
  legacySourceFields,
} from "./autoForwardSources";
import "./AutoForwardSettings.css";

/**
 * AutoForwardSettings — Admin → Auto-Forward.
 *
 * Lets the workspace owner mirror everything that lands in one or more of
 * their mailboxes onto up to three other addresses (another of their inboxes,
 * a partner, ...). The copy is the original message — same subject, same body —
 * so a reply from the destination reads as a normal continuation. Rules are
 * stored at `/World/{userId}/settings/autoForwardRules/{ruleId}` and executed
 * by the `onInboxMailAutoForward` cloud function — the UI only edits rules, it
 * never sends anything itself.
 *
 * Each rule is saved individually and immediately (like the cleanup guidelines),
 * so this tab is excluded from the global "Save Settings" button.
 */

const MAX_TARGETS = 3;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors the AI `enthusiasmScore` scale (1-10) the backend filters on.
const SCORE_OPTIONS = [
  { value: 8, label: "Rating 8+ — only clearly enthusiastic replies" },
  { value: 6, label: "Rating 6+ — interested and positive replies" },
  { value: 5, label: "Rating 5+ — neutral and better" },
  { value: 3, label: "Rating 3+ — everything except clearly negative" },
];

const emptyRule = () => ({
  enabled: true,
  name: "",
  sources: [],
  sourceAccountId: "",
  sourceEmail: "",
  targets: [""],
  minScore: null,
  createdAt: Date.now(),
});

const AutoForwardSettings = ({ userId }) => {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;

    const rulesRef = ref(database, `/World/${userId}/settings/autoForwardRules`);
    return onValue(
      rulesRef,
      (snapshot) => {
        const value = snapshot.val() || {};
        const list = Object.keys(value).map((id) => {
          const rule = value[id] || {};
          const sources = hydrateAutoForwardSources(rule);
          const legacy = legacySourceFields(sources);
          return {
            id,
            enabled: rule.enabled !== false,
            name: rule.name || "",
            sources,
            sourceAccountId: legacy.sourceAccountId,
            sourceEmail: legacy.sourceEmail,
            targets: Array.isArray(rule.targets) && rule.targets.length > 0 ? rule.targets : [""],
            minScore: typeof rule.minScore === "number" ? rule.minScore : null,
            createdAt: rule.createdAt || 0,
          };
        });
        list.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        setRules(list);
      },
      (err) => {
        console.error("[AutoForward] rule load failed:", err);
        setError("Could not load forwarding rules.");
      }
    );
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    const accountsRef = collection(firestore, "world_users", userId, "emailAccounts");
    return onSnapshot(
      accountsRef,
      (snapshot) => {
        setAccounts(
          snapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((account) => !!account.email)
            .sort((a, b) => a.email.localeCompare(b.email))
        );
      },
      (err) => {
        console.error("[AutoForward] account load failed:", err);
      }
    );
  }, [userId]);

  /**
   * Validates a rule before it can be saved. Returns an error string, or '' when
   * the rule is sane — the same conditions the backend enforces, so a saved rule
   * always actually fires.
   */
  const validate = useCallback((rule) => {
    const sources = hydrateAutoForwardSources(rule);
    if (sources.length === 0) return "Please pick at least one mailbox to forward from.";

    const targets = rule.targets.map((t) => (t || "").trim().toLowerCase()).filter(Boolean);
    if (targets.length === 0) return "Please add at least one destination address.";
    if (targets.some((t) => !EMAIL_RE.test(t))) return "One of the destination addresses is not a valid email.";

    const sourceEmails = sourceEmailsOf(sources);
    if (targets.some((t) => sourceEmails.includes(t))) {
      return "The destination cannot be one of the source mailboxes.";
    }
    if (new Set(targets).size !== targets.length) return "The same destination is listed twice.";

    return "";
  }, []);

  const persist = useCallback(
    async (ruleId, rule) => {
      const validationError = validate(rule);
      if (validationError) {
        setError(validationError);
        return false;
      }

      setError("");
      setSavingId(ruleId);
      try {
        const sources = hydrateAutoForwardSources(rule);
        const legacy = legacySourceFields(sources);
        const payload = {
          enabled: rule.enabled !== false,
          name: (rule.name || "").trim(),
          sources,
          sourceAccountId: legacy.sourceAccountId,
          sourceEmail: legacy.sourceEmail,
          targets: rule.targets
            .map((t) => (t || "").trim().toLowerCase())
            .filter(Boolean)
            .slice(0, MAX_TARGETS),
          minScore: typeof rule.minScore === "number" ? rule.minScore : null,
          createdAt: rule.createdAt || Date.now(),
          updatedAt: Date.now(),
        };
        await set(ref(database, `/World/${userId}/settings/autoForwardRules/${ruleId}`), payload);
        return true;
      } catch (err) {
        console.error("[AutoForward] save failed:", err);
        setError("Could not save the rule: " + err.message);
        return false;
      } finally {
        setSavingId(null);
      }
    },
    [userId, validate]
  );

  const handleCreate = async () => {
    const ruleId = `rule_${Date.now()}`;
    const saved = await persist(ruleId, draft);
    if (saved) setDraft(null);
  };

  const handleUpdate = async () => {
    const saved = await persist(editing.id, editing);
    if (saved) setEditing(null);
  };

  const handleToggle = async (rule) => {
    try {
      await update(ref(database, `/World/${userId}/settings/autoForwardRules/${rule.id}`), {
        enabled: !rule.enabled,
        updatedAt: Date.now(),
      });
    } catch (err) {
      console.error("[AutoForward] toggle failed:", err);
      setError("Could not update the rule: " + err.message);
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await remove(ref(database, `/World/${userId}/settings/autoForwardRules/${ruleId}`));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("[AutoForward] delete failed:", err);
      setError("Could not delete the rule: " + err.message);
    }
  };

  const sourceLabels = (rule) =>
    hydrateAutoForwardSources(rule).map((source) => {
      const account = accounts.find((a) => a.id === source.accountId);
      return account ? account.email : source.email || "Unknown mailbox";
    });

  const scoreLabel = (minScore) => (typeof minScore === "number" ? `Rating ${minScore}+` : "All replies");

  const renderEditor = (rule, onChange) => {
    const sources = hydrateAutoForwardSources(rule);
    const allSelected = accounts.length > 0 && accounts.every((account) =>
      sources.some((source) => source.accountId === account.id)
    );
    const setTarget = (index, value) => {
      const targets = [...rule.targets];
      targets[index] = value;
      onChange({ ...rule, targets });
    };

    return (
      <div className="af-editor">
        <div className="af-field">
          <label className="af-label">
            Forward from mailboxes{" "}
            <span className="af-label-meta">
              {sources.length === 0
                ? "pick one or more"
                : `${sources.length} selected`}
            </span>
          </label>
          {accounts.length > 0 && (
            <div className="af-mailbox-toolbar">
              <button
                type="button"
                className="af-text-btn"
                onClick={() => {
                  const next = allSelected
                    ? []
                    : accounts.map((account) => ({
                        accountId: account.id,
                        email: (account.email || "").trim().toLowerCase(),
                      }));
                  onChange({ ...rule, sources: next, ...legacySourceFields(next) });
                }}
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </div>
          )}
          <div className="af-mailbox-list" role="group" aria-label="Source mailboxes">
            {accounts.map((account) => {
              const on = sources.some((source) => source.accountId === account.id);
              return (
                <button
                  type="button"
                  key={account.id}
                  className={`af-mailbox-opt${on ? " active" : ""}`}
                  onClick={() => {
                    const next = toggleAutoForwardSource(sources, account);
                    onChange({ ...rule, sources: next, ...legacySourceFields(next) });
                  }}
                  aria-pressed={on}
                >
                  <span className="af-checkdot" aria-hidden="true" />
                  {account.email}
                </button>
              );
            })}
          </div>
          {accounts.length === 0 && (
            <span className="af-hint">
              No mailboxes connected yet — add one under Email → Accounts first.
            </span>
          )}
        </div>

        <div className="af-field">
          <label className="af-label">
            Forward to <span className="af-label-meta">up to {MAX_TARGETS} addresses</span>
          </label>
          {rule.targets.map((target, index) => (
            <div className="af-target-row" key={index}>
              <input
                type="email"
                className="af-input"
                value={target}
                onChange={(e) => setTarget(index, e.target.value)}
                placeholder="partner@company.com"
              />
              {rule.targets.length > 1 && (
                <button
                  type="button"
                  className="af-icon-btn danger"
                  onClick={() => onChange({ ...rule, targets: rule.targets.filter((_, i) => i !== index) })}
                  title="Remove this address"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {rule.targets.length < MAX_TARGETS && (
            <button
              type="button"
              className="altBtn altBtn--petrol"
              onClick={() => onChange({ ...rule, targets: [...rule.targets, ""] })}
            >
              + Add address
            </button>
          )}
        </div>

        <div className="af-field">
          <label className="af-label">Which mails to forward</label>
          <select
            className="af-select"
            value={typeof rule.minScore === "number" ? String(rule.minScore) : "all"}
            onChange={(e) =>
              onChange({
                ...rule,
                minScore: e.target.value === "all" ? null : Number(e.target.value),
              })
            }
          >
            <option value="all">All replies</option>
            {SCORE_OPTIONS.map((option) => (
              <option key={option.value} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="af-hint">
            The rating is the AI reply score (1–10) the inbox already calculates for every reply.
          </span>
        </div>

        <div className="af-field">
          <label className="af-label">
            Name <span className="af-label-meta">optional</span>
          </label>
          <input
            type="text"
            className="af-input"
            value={rule.name}
            onChange={(e) => onChange({ ...rule, name: e.target.value })}
            placeholder="e.g. Partner Müller"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="af">
      <header className="af-page-head">
        <div>
          <h3 className="af-page-title">Auto-Forward</h3>
          <p className="af-page-sub">
            Mirror incoming mail onto another of your addresses as the original
            message — same subject, same body, no Fwd: wrapper. A reply from that
            inbox looks like a normal continuation to the requester.
          </p>
        </div>
        <div className="af-page-head-actions">
          <span className="af-badge">
            {rules.length} {rules.length === 1 ? "rule" : "rules"}
          </span>
          {!draft && !editing && (
            <button type="button" className="altBtn altBtn--save" onClick={() => setDraft(emptyRule())}>
              <i className="fa fa-plus" /> Add rule
            </button>
          )}
        </div>
      </header>

      <div className="af-notice">
        <i className="fa fa-shield" />
        <div>
          <strong>Silent copy:</strong> the destination receives the original subject and body —
          no “Fwd:”, no forwarded-message headers. Reply-To is the original sender, so Reply
          from that inbox goes to them and quotes a normal thread. Warmup, bounces and spam
          are never forwarded. Attachments stay in the source mailbox.
        </div>
      </div>

      {error && (
        <div className="af-error">
          <i className="fa fa-exclamation-circle" /> {error}
        </div>
      )}

      <section className="af-section">
        <div className="af-list">
          {rules.length === 0 && !draft && (
            <div className="af-empty">
              <i className="fa fa-share" />
              <span>No forwarding rules yet.</span>
            </div>
          )}

          {rules.map((rule) =>
            editing && editing.id === rule.id ? (
              <div className="af-card editing" key={rule.id}>
                {renderEditor(editing, setEditing)}
                <div className="af-editor-actions">
                  <button
                    type="button"
                    className="altBtn altBtn--petrol"
                    onClick={() => {
                      setEditing(null);
                      setError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="altBtn altBtn--save"
                    onClick={handleUpdate}
                    disabled={savingId !== null}
                  >
                    {savingId !== null ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            ) : (
              <div className={`af-card ${rule.enabled ? "" : "disabled"}`} key={rule.id}>
                <div className="af-card-head">
                  <div className="af-card-flow">
                    <span className="af-chip-group">
                      {sourceLabels(rule).map((email) => (
                        <span className="af-chip" key={email}>
                          {email}
                        </span>
                      ))}
                    </span>
                    <i className="fa fa-long-arrow-right af-arrow" />
                    <span className="af-chip-group">
                      {rule.targets.filter(Boolean).map((target) => (
                        <span className="af-chip target" key={target}>
                          {target}
                        </span>
                      ))}
                    </span>
                  </div>

                  <div className="af-card-actions">
                    <span className="af-score">{scoreLabel(rule.minScore)}</span>
                    <label className="af-switch" title={rule.enabled ? "Active" : "Paused"}>
                      <input type="checkbox" checked={rule.enabled} onChange={() => handleToggle(rule)} />
                      <span className="af-switch-track" />
                    </label>
                    <button
                      type="button"
                      className="af-icon-btn"
                      onClick={() => {
                        setError("");
                        setDraft(null);
                        setEditing({ ...rule });
                      }}
                      title="Edit rule"
                    >
                      <i className="fa fa-pencil" />
                    </button>
                    <button
                      type="button"
                      className="af-icon-btn danger"
                      onClick={() => setConfirmDeleteId(rule.id)}
                      title="Delete rule"
                    >
                      <i className="fa fa-trash-o" />
                    </button>
                  </div>
                </div>

                {rule.name && <div className="af-card-name">{rule.name}</div>}

                {confirmDeleteId === rule.id && (
                  <div className="af-confirm">
                    <span>Delete this rule?</span>
                    <button type="button" className="altBtn altBtn--save" onClick={() => handleDelete(rule.id)}>
                      Delete
                    </button>
                    <button type="button" className="altBtn altBtn--petrol" onClick={() => setConfirmDeleteId(null)}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )
          )}

          {draft && (
            <div className="af-card editing">
              {renderEditor(draft, setDraft)}
              <div className="af-editor-actions">
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={() => {
                    setDraft(null);
                    setError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="altBtn altBtn--save"
                  onClick={handleCreate}
                  disabled={savingId !== null}
                >
                  {savingId !== null ? "Saving…" : "Create rule"}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AutoForwardSettings;
